import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Form,
  Select,
  InputNumber,
  Input,
  Button,
  Space,
  message,
  Typography,
  List,
  Card,
  Tag,
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import { stockService } from '../../services/stockService';
import CustomerSelect from '../customers/CustomerSelect';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface StockOutModalMobileProps {
  visible: boolean;
  onCancel: () => void;
  editingRecord?: any;
  medicines: any[];
  isLoadingDetail?: boolean;
}

interface StockOutItem {
  medicine_id: number;
  lot_id: number;
  quantity: number;
  unit_price: number;
  lot_number: string;
  expiry_date: any;
  notes?: string;
}

const StockOutModalMobile: React.FC<StockOutModalMobileProps> = ({
  visible,
  onCancel,
  editingRecord,
  medicines,
  isLoadingDetail = false
}) => {
  const [form] = Form.useForm();
  const [itemForm] = Form.useForm();
  const [items, setItems] = useState<StockOutItem[]>([]);
  const [lots, setLots] = useState<any[]>([]);
  const [isPromotion, setIsPromotion] = useState(false);
  const queryClient = useQueryClient();

  // Mutations
  const createMutation = useMutation(
    (data: any) => stockService.createStockOutVoucher(data),
    {
      onSuccess: () => {
        message.success('Tạo phiếu xuất kho thành công!');
        queryClient.invalidateQueries('stock-out');
        handleCancelInternal();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi tạo phiếu xuất kho');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: any }) => stockService.updateStockOutVoucher(id, data),
    {
      onSuccess: () => {
        message.success('Cập nhật phiếu xuất kho thành công!');
        queryClient.invalidateQueries('stock-out');
        handleCancelInternal();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật phiếu xuất kho');
      },
    }
  );

  useEffect(() => {
    if (visible) {
      if (editingRecord && !isLoadingDetail) {
        let voucherData, itemsData;
        if (editingRecord.voucher && editingRecord.items) {
          voucherData = editingRecord.voucher;
          itemsData = editingRecord.items;
        } else {
          setItems([]);
          setIsPromotion(false);
          return;
        }
        const processedItems = itemsData.map((item: any) => ({
          medicine_id: item.medicine_id,
          lot_id: item.lot_id,
          quantity: item.quantity || 0,
          unit_price: item.unit_price || 0,
          lot_number: item.lot_number || '',
          expiry_date: item.expiry_date ? dayjs(item.expiry_date) : null,
          notes: item.notes || ''
        }));
        form.setFieldsValue({
          customer_name: voucherData.customer_name,
          customer_phone: voucherData.customer_phone,
          customer_address: voucherData.customer_address,
          notes: voucherData.notes || ''
        });
        setItems(processedItems);
        setIsPromotion(false);
      } else if (!editingRecord && !isLoadingDetail) {
        form.resetFields();
        setItems([]);
        setIsPromotion(false);
      }
    } else {
      form.resetFields();
      setItems([]);
      setIsPromotion(false);
    }
  }, [visible, editingRecord, isLoadingDetail]);

  const handleCancelInternal = () => {
    form.resetFields();
    itemForm.resetFields();
    setItems([]);
    setIsPromotion(false);
    onCancel();
  };

  const handleAddItem = async () => {
    try {
      const values = await itemForm.validateFields();
      const medicine = medicines.find(m => m.id === values.medicine_id);
      const lot = lots.find(l => l.lot_id === values.lot_id);
      if (!medicine || !lot) return;
      const newItem: StockOutItem = {
        medicine_id: values.medicine_id,
        lot_id: values.lot_id,
        quantity: values.quantity,
        unit_price: isPromotion ? 0 : values.unit_price,
        lot_number: lot.lot_number,
        expiry_date: lot.expiry_date ? dayjs(lot.expiry_date) : null,
        notes: values.notes || ''
      };
      setItems([...items, newItem]);
      itemForm.resetFields();
      setLots([]);
      setIsPromotion(false);
    } catch {}
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleMedicineChange = async (medicineId: number) => {
    try {
      const stockLots = await stockService.getMedicineStock(medicineId);
      setLots(stockLots);
    } catch {
      setLots([]);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (items.length === 0) {
        message.error('Vui lòng thêm ít nhất một thuốc');
        return;
      }
      const data = {
        customer_name: values.customer_name,
        customer_phone: values.customer_phone,
        customer_address: values.customer_address,
        notes: values.notes || '',
        items: items.map((item: StockOutItem) => ({
          medicine_id: item.medicine_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          lot_id: item.lot_id,
          lot_number: item.lot_number,
          notes: item.notes || ''
        }))
      };
      if (editingRecord) {
        const id = editingRecord.voucher?.id || editingRecord.id;
        await updateMutation.mutateAsync({ id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
    } catch (error) {}
  };

  const totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  return (
    <Drawer
      title={editingRecord ? 'Sửa phiếu xuất kho' : 'Tạo phiếu xuất kho'}
      open={visible}
      onClose={handleCancelInternal}
      placement="bottom"
      height="90vh"
      bodyStyle={{ padding: 12, background: '#f5f6fa' }}
      destroyOnClose
      className="stockout-mobile-modal"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ width: '100%' }}
      >
        <Card style={{ marginBottom: 12, borderRadius: 10 }}>
          <Form.Item
            name="customer_name"
            label="Khách hàng"
            rules={[{ required: true, message: 'Vui lòng chọn khách hàng' }]}
          >
            <CustomerSelect
              placeholder="Chọn khách hàng"
              onChange={(_value, customer) => {
                if (customer) {
                  form.setFieldsValue({
                    customer_phone: customer.phone || '',
                    customer_address: customer.address || ''
                  });
                }
              }}
            />
          </Form.Item>
          <Form.Item name="customer_phone" label="Số điện thoại" rules={[{ required: true, message: 'Nhập số điện thoại' }]}> 
            <Input style={{ width: '100%' }} placeholder="Số điện thoại" />
          </Form.Item>
          <Form.Item name="customer_address" label="Địa chỉ" rules={[{ required: true, message: 'Nhập địa chỉ' }]}> 
            <Input style={{ width: '100%' }} placeholder="Địa chỉ" />
          </Form.Item>
          <Form.Item name="notes" label="Ghi chú">
            <TextArea rows={2} placeholder="Nhập ghi chú (nếu có)" />
          </Form.Item>
        </Card>
        <Card style={{ marginBottom: 12, borderRadius: 10 }}>
          <Title level={5} style={{ marginBottom: 8 }}>Thêm thuốc</Title>
          <Form form={itemForm} layout="vertical">
            <Form.Item
              name="medicine_id"
              label="Thuốc"
              rules={[{ required: true, message: 'Chọn thuốc' }]}
            >
              <Select placeholder="Chọn thuốc" onChange={handleMedicineChange}>
                {medicines.map(medicine => (
                  <Option key={medicine.id} value={medicine.id}>{medicine.name} ({medicine.code})</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="lot_id" label="Lô thuốc" rules={[{ required: true, message: 'Chọn lô thuốc' }]}> 
              <Select placeholder="Chọn lô thuốc">
                {lots.map(lot => (
                  <Option key={lot.lot_id} value={lot.lot_id}>{lot.lot_number} - HSD: {dayjs(lot.expiry_date).format('DD/MM/YYYY')} (Còn: {lot.available_quantity})</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="quantity" label="Số lượng" rules={[{ required: true, message: 'Nhập số lượng' }]}> 
              <InputNumber min={1} style={{ width: '100%' }} placeholder="Số lượng" />
            </Form.Item>
            <Form.Item>
              <Checkbox 
                checked={isPromotion} 
                onChange={e => setIsPromotion(e.target.checked)}
                style={{ marginBottom: 8 }}
              >
                Hàng khuyến mãi (giá = 0)
              </Checkbox>
            </Form.Item>
            <Form.Item 
              name="unit_price" 
              label="Đơn giá" 
              rules={[
                { 
                  required: !isPromotion, 
                  message: 'Nhập đơn giá' 
                }
              ]}
            > 
              <InputNumber 
                min={0} 
                style={{ width: '100%' }} 
                placeholder="Đơn giá"
                disabled={isPromotion}
                value={isPromotion ? 0 : undefined}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value: string | undefined) => value ? Number(value.replace(/\$\s?|,*/g, '')) : 0}
              />
            </Form.Item>
            <Form.Item name="notes" label="Ghi chú">
              <Input style={{ width: '100%' }} placeholder="Ghi chú (nếu có)" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddItem} block>Thêm thuốc</Button>
            </Form.Item>
          </Form>
        </Card>
        <Card style={{ marginBottom: 12, borderRadius: 10 }}>
          <Title level={5} style={{ marginBottom: 8 }}>Danh sách thuốc</Title>
          <List
            dataSource={items}
            renderItem={(item, idx) => {
              const medicine = medicines.find(m => m.id === item.medicine_id);
              return (
                <List.Item>
                  <Card style={{ 
                    width: '100%', 
                    borderRadius: 8, 
                    marginBottom: 0,
                    position: 'relative',
                    paddingTop: 8
                  }}>
                    {/* Nút xóa ở góc phải */}
                    <Button 
                      type="primary" 
                      danger 
                      icon={<DeleteOutlined />} 
                      onClick={() => handleRemoveItem(idx)} 
                      size="small"
                      shape="circle"
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        height: 28,
                        boxShadow: '0 2px 4px rgba(255, 77, 79, 0.2)',
                        border: 'none',
                        transition: 'all 0.2s ease-in-out',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(255, 77, 79, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(255, 77, 79, 0.2)';
                      }}
                    />
                    
                    <div style={{ fontWeight: 500, paddingRight: 40 }}>
                      {medicine?.name || 'N/A'} <Tag>{medicine?.code}</Tag>
                      {item.unit_price === 0 && (
                        <Tag color="red" style={{ marginLeft: 4 }}>Khuyến mãi</Tag>
                      )}
                    </div>
                    <div style={{ fontSize: 13 }}>
                      Số lượng: <b>{item.quantity}</b> | 
                      Đơn giá: <b>{item.unit_price === 0 ? 'Miễn phí' : `${item.unit_price.toLocaleString('vi-VN')}đ`}</b> | 
                      Số lô: <b>{item.lot_number}</b>
                    </div>
                    <div style={{ fontSize: 12, color: '#888' }}>HSD: {item.expiry_date ? dayjs(item.expiry_date).format('DD/MM/YYYY') : 'N/A'}</div>
                    {item.notes && <div style={{ fontSize: 12, color: '#888' }}>Ghi chú: {item.notes}</div>}
                  </Card>
                </List.Item>
              );
            }}
            locale={{ emptyText: 'Chưa có thuốc nào' }}
          />
        </Card>
        <Card style={{ borderRadius: 10, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text strong>Tổng tiền:</Text>
            <Text strong style={{ color: '#52c41a', fontSize: 18 }}>{totalAmount.toLocaleString('vi-VN')} VNĐ</Text>
          </div>
          {items.filter(item => item.unit_price === 0).length > 0 && (
            <div style={{ fontSize: 12, color: '#999', textAlign: 'right' }}>
              (Có {items.filter(item => item.unit_price === 0).length} sản phẩm khuyến mãi)
            </div>
          )}
        </Card>
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={handleCancelInternal}>Hủy</Button>
          <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={createMutation.isLoading || updateMutation.isLoading}>{editingRecord ? 'Cập nhật' : 'Lưu phiếu'}</Button>
        </Space>
      </Form>
    </Drawer>
  );
};

export default StockOutModalMobile;
